import { klaviyoSignup } from '~/queries'

/**
 * Catches the Klaviyo popup's signups on their way past (BOAT-1894).
 *
 * The "Enjoy 5% off your first booking" popup is Klaviyo's own hosted form,
 * injected by klaviyo.js through the `script_sources` site setting on Ticketed.
 * Everything it collects goes to Klaviyo and, until this listener, nowhere
 * else — while it fires at first landing, the earliest moment a visitor's UTMs
 * are still in front of us.
 *
 * Klaviyo's onsite JS dispatches a `klaviyoForms` CustomEvent on the window for
 * every form interaction; `detail.type === 'submit'` is the one that carries the
 * submitted fields in `detail.metaData`. (Verified against Klaviyo's developer
 * docs, "Track Klaviyo form activity using JavaScript", and against the live
 * form definition for company V6JGDe, whose fields are `$email` and
 * `$first_name`.)
 *
 * What we send is the address plus OUR touch cookies. Klaviyo's own UTM capture
 * is not configured — the live form has no hidden UTM fields — and we would not
 * want to depend on it anyway: the first-touch cookie was written on the
 * visitor's first tagged arrival, which may be days before they subscribed.
 */

/** Klaviyo keys submitted fields by the form's field id, e.g. `$email`. */
const EMAIL_KEYS = ['$email', 'email', 'Email', 'EMAIL']
const NAME_KEYS = ['$first_name', 'first_name', 'firstName', '$name', 'name']

type KlaviyoFormsDetail = {
  type?: string
  formId?: string
  metaData?: Record<string, unknown>
}

const readString = (
  metaData: Record<string, unknown>,
  keys: string[],
): string | undefined => {
  for (const key of keys) {
    const value = metaData[key]

    if (typeof value === 'string' && value.trim() !== '') return value.trim()
  }

  return undefined
}

export default defineNuxtPlugin(() => {
  if (typeof window === 'undefined') return

  const { utmPayload } = useOrderUtm()
  const { devicePayload } = useDeviceSignal()

  /**
   * A multi-step form emits `stepSubmit` per step and `submit` at the end, so
   * the same address can be announced twice. The server upserts, but there is
   * no reason to make it prove that on every signup.
   */
  const sent = new Set<string>()

  const onKlaviyoForm = async (event: Event) => {
    const detail = (event as CustomEvent<KlaviyoFormsDetail>).detail

    if (!detail) return
    if (detail.type !== 'submit' && detail.type !== 'stepSubmit') return

    const metaData = detail.metaData

    if (!metaData || typeof metaData !== 'object') return

    const email = readString(metaData, EMAIL_KEYS)

    // A step that collected no address is not a signup — the visitor is still
    // filling the form in.
    if (!email) return

    const key = email.toLowerCase()

    if (sent.has(key)) return

    sent.add(key)

    // The popup fires at first landing, so this is usually the earliest
    // moment this browser stops being anonymous (BOAT-1895).
    klaviyoSignup({
      email,
      name: readString(metaData, NAME_KEYS),
      ...utmPayload(),
      ...(await devicePayload()),
    }).catch(() => {
      // The visitor is reading their discount code and owes us nothing. Let
      // them retry on a later submit rather than surface anything.
      sent.delete(key)
    })
  }

  window.addEventListener('klaviyoForms', (event) => void onKlaviyoForm(event))
})
